import Add from '@mui/icons-material/Add';
import { paperClasses } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useMemo, useRef, useState } from 'react';

import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { PaymentTerm } from '~hooks/usePaymentTerms/models';
import usePaymentTerms from '~hooks/usePaymentTerms/usePaymentTerms';
import { RutterPaymentTerm } from '~hooks/useRutter/models';
import PaymentTermForm, {
  PaymentTermFormHandler,
} from '~pages/Settings/PaymentTermsManagement/PaymentTermForm';
import {
  getPaymentTermAsyncErrors,
  PaymentTermDtoAsyncError,
} from '~pages/Settings/PaymentTermsManagement/paymentTermSchema';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  MAPPING_COLUMN_MIN_WIDTH_PX,
  MAPPING_SELECT_WIDTH_PX,
} from './mappingColumnWidth';
import MappingSelect, { QuickBooksOption } from './MappingSelect';
import MappingTabToolbar, { MappingStatusFilterValue } from './MappingTabToolbar';
import { useQuickBooksMappingQueries } from './QuickBooksMappingQueryContext';

const I18N_PREFIX = 'administration.integration.rutter.quick_books_online';

interface PaymentTermMappingRow {
  id: string;
  paymentTerm: PaymentTerm;
}

function toQuickBooksOption(qboTerm: RutterPaymentTerm): QuickBooksOption {
  return {
    id: qboTerm.id,
    name: t(`${I18N_PREFIX}.tread_first.payment_term_option`, {
      count: qboTerm.dueDays ?? 0,
      name: qboTerm.termName ?? '',
    }),
  };
}

export default function PaymentTermsTab() {
  const {
    isLoading,
    qboPaymentTerms,
    refetchPaymentTerms,
    savePaymentTermMapping,
    treadPaymentTerms,
  } = useQuickBooksMappingQueries();
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { canCreateRate } = userStore.getPermissions();
  const { create: createPaymentTerm } = usePaymentTerms();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createAsyncErrors, setCreateAsyncErrors] = useState<PaymentTermDtoAsyncError[]>(
    [],
  );
  const paymentTermFormRef = useRef<PaymentTermFormHandler>(null);

  // the dialog header's close button and Escape both call this directly, so the no-op
  // while creating has to live here rather than only on the action row's Cancel button
  const closeCreateDialog = useCallback(() => {
    if (isCreating) {
      return;
    }

    setIsCreateDialogOpen(false);
    setCreateAsyncErrors([]);
  }, [isCreating]);

  const handleCreateSubmit = useCallback(async () => {
    const formData = await paymentTermFormRef.current?.onSubmit();
    if (!formData || !companyId) {
      return;
    }

    setCreateAsyncErrors([]);
    setIsCreating(true);

    try {
      await createPaymentTerm(companyId, {
        name: formData.name,
        due_days: Number(formData.dueDays),
      });
      toasterStore.push(
        alert(t('administration.payment_terms.saved'), AlertTypes.success),
      );
      setIsCreateDialogOpen(false);
      await refetchPaymentTerms();
    } catch (error) {
      setCreateAsyncErrors(getPaymentTermAsyncErrors(error));
    } finally {
      setIsCreating(false);
    }
  }, [companyId, createPaymentTerm, refetchPaymentTerms, toasterStore]);

  const qboTermsByPaymentTermId = useMemo(() => {
    return new Map(
      qboPaymentTerms.flatMap((qboTerm) => {
        const mappedTermId = qboTerm.paymentTerm?.id;

        return mappedTermId ? [[mappedTermId, qboTerm] as const] : [];
      }),
    );
  }, [qboPaymentTerms]);

  // an inactive term is hidden from pickers, but dropping one that still backs a mapping
  // would orphan that mapping where nobody can see or clear it
  const visibleTerms = useMemo(() => {
    return treadPaymentTerms.filter(
      (term) => term.active || qboTermsByPaymentTermId.has(term.id),
    );
  }, [treadPaymentTerms, qboTermsByPaymentTermId]);

  const unmappedCount = useMemo(() => {
    return visibleTerms.filter((term) => !qboTermsByPaymentTermId.has(term.id)).length;
  }, [visibleTerms, qboTermsByPaymentTermId]);

  const rows: PaymentTermMappingRow[] = useMemo(() => {
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();

    return visibleTerms
      .filter((term) => {
        const matchesSearch =
          !normalizedSearchQuery ||
          term.name.toLowerCase().includes(normalizedSearchQuery);
        const rowStatus = qboTermsByPaymentTermId.has(term.id)
          ? MappingStatusFilterValue.MAPPED
          : MappingStatusFilterValue.UNMAPPED;
        const matchesStatusFilter =
          statusFilter.length === 0 || statusFilter.includes(rowStatus);

        return matchesSearch && matchesStatusFilter;
      })
      .sort((termA, termB) => termA.dueDays - termB.dueDays)
      .map((term) => ({ id: term.id, paymentTerm: term }));
  }, [visibleTerms, searchQuery, statusFilter, qboTermsByPaymentTermId]);

  // one-to-one: a QuickBooks term already backing another Tread term is not offered. A
  // term without a numeric due_days is date-driven in QuickBooks and has no Tread
  // equivalent, so the backend rejects it — never offer it either. Built once per
  // qboPaymentTerms change rather than per cell render, since the table renders a
  // selector for every row at once.
  const freeOptions = useMemo(() => {
    return qboPaymentTerms
      .filter((qboTerm) => qboTerm.dueDays !== null && !qboTerm.paymentTerm)
      .map(toQuickBooksOption)
      .sort((optionA, optionB) => optionA.name.localeCompare(optionB.name));
  }, [qboPaymentTerms]);

  const optionsByMappedPaymentTermId = useMemo(() => {
    return new Map(
      Array.from(qboTermsByPaymentTermId.entries()).map(([paymentTermId, qboTerm]) => {
        const options = [...freeOptions, toQuickBooksOption(qboTerm)].sort(
          (optionA, optionB) => optionA.name.localeCompare(optionB.name),
        );

        return [paymentTermId, options] as const;
      }),
    );
  }, [qboTermsByPaymentTermId, freeOptions]);

  const optionsForRow = (row: PaymentTermMappingRow) => {
    return optionsByMappedPaymentTermId.get(row.id) ?? freeOptions;
  };

  const handleSave = async (
    row: PaymentTermMappingRow,
    option: Nullable<QuickBooksOption>,
  ) => {
    const saved = await savePaymentTermMapping(row.id, option?.id ?? null);

    if (saved) {
      toasterStore.push(
        alert(
          t(`${I18N_PREFIX}.grid.tread_name_updated`, { name: row.paymentTerm.name }),
          AlertTypes.success,
        ),
      );
    }

    return saved;
  };

  const columns: Array<DataTableColumn<PaymentTermMappingRow>> = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: t(`${I18N_PREFIX}.tread_first.columns.tread_payment_term`),
        valueGetter: (row) => row.paymentTerm.name,
      },
      {
        field: 'dueDays',
        headerName: t(`${I18N_PREFIX}.tread_first.columns.due_days`),
        valueGetter: (row) => row.paymentTerm.dueDays,
      },
      {
        field: 'mapping',
        headerName: t(`${I18N_PREFIX}.tread_first.columns.quick_books_payment_term`),
        minWidth: MAPPING_COLUMN_MIN_WIDTH_PX,
        renderCell: (row) => {
          const mappedQboTerm = qboTermsByPaymentTermId.get(row.id);

          return (
            <MappingSelect
              dataTestId={`quick-books-mapping-select-${row.id}`}
              onSave={(option) => handleSave(row, option)}
              options={optionsForRow(row)}
              width={MAPPING_SELECT_WIDTH_PX}
              value={mappedQboTerm ? toQuickBooksOption(mappedQboTerm) : null}
            />
          );
        },
      },
    ];
  }, [qboPaymentTerms, qboTermsByPaymentTermId]);

  return (
    <>
      <MappingTabToolbar
        actions={
          <SmallButton
            color="brandV2Yellow"
            disabled={!canCreateRate}
            onClick={() => setIsCreateDialogOpen(true)}
            startIcon={<Add />}
          >
            {t(`${I18N_PREFIX}.tread_first.new_payment_term`)}
          </SmallButton>
        }
        mappedCount={visibleTerms.length - unmappedCount}
        onSearchQueryChange={setSearchQuery}
        onStatusFilterChange={setStatusFilter}
        searchQuery={searchQuery}
        statusFilter={statusFilter}
        unmappedCount={unmappedCount}
      />

      <DataTable
        columns={columns}
        hideToolbar
        id="quick-books-mapping-payment-terms-grid"
        loading={isLoading}
        rows={rows}
      />

      <Dialog
        isOpen={isCreateDialogOpen}
        onClose={closeCreateDialog}
        preventCloseOnBackdropClick
        sx={{ [`.${paperClasses.root}`]: { maxWidth: '700px' } }}
      >
        <DialogHeader onClose={closeCreateDialog}>
          {t('administration.payment_terms.create_term')}
        </DialogHeader>

        <DialogContent>
          <PaymentTermForm ref={paymentTermFormRef} asyncErrors={createAsyncErrors} />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isCreating}
            onClick={closeCreateDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            disabled={isCreating}
            loading={isCreating}
            onClick={handleCreateSubmit}
          >
            {t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
